import { Howl, Howler } from 'howler'
import { watch } from 'vue'
import { useSoundStore, type BgmTrack } from '@/stores/sound'

export type SoundId =
  | 'check' | 'chip_bet' | 'chip_call' | 'fold' | 'your_turn'
  | 'shuffle' | 'deal_single' | 'deal_three' | 'pot_collect' | 'allin_crowd'
  | 'chat_kapuchino' | 'chat_capixie' | 'chat_yanpai'
  | 'chat_xiaobiesan' | 'chat_wolaitongniwan' | 'chat_canshi' | 'chat_zhefu'

const base = import.meta.env.BASE_URL.replace(/\/$/, '')

const MANIFEST: Record<SoundId, string> = {
  check:                `${base}/sounds/check.mp3`,
  chip_bet:             `${base}/sounds/chip_bet.mp3`,
  chip_call:            `${base}/sounds/chip_call.mp3`,
  fold:                 `${base}/sounds/fold_card.mp3`,
  your_turn:            `${base}/sounds/your_turn.mp3`,
  shuffle:              `${base}/sounds/shuffle.mp3`,
  deal_single:          `${base}/sounds/deal_single.mp3`,
  deal_three:           `${base}/sounds/deal_three.mp3`,
  pot_collect:          `${base}/sounds/pot_collect.mp3`,
  allin_crowd:          `${base}/sounds/allin_crowd.mp3`,
  chat_kapuchino:       `${base}/sounds/chat_kapuchino.mp3`,
  chat_capixie:         `${base}/sounds/chat_capixie.mp3`,
  chat_yanpai:          `${base}/sounds/chat_yanpai.mp3`,
  chat_xiaobiesan:      `${base}/sounds/chat_xiaobiesan.mp3`,
  chat_wolaitongniwan:  `${base}/sounds/chat_wolaitongniwan.mp3`,
  chat_canshi:          `${base}/sounds/chat_canshi.mp3`,
  chat_zhefu:           `${base}/sounds/chat_zhefu.mp3`,
}

const BGM_FILES: Record<Exclude<BgmTrack, 'none'>, string> = {
  jazz: `${base}/sounds/bgm_jazz.mp3`,
  lofi: `${base}/sounds/bgm_lofi.mp3`,
  edm:  `${base}/sounds/bgm_edm.mp3`,
}

const sfxPool = new Map<SoundId, Howl>()
let bgm: Howl | null = null
let initialized = false
let watchStops: Array<() => void> = []

// 将滑块线性值(0~1)转为对数感知音量，使人耳听感均匀
function toPerceptual(v: number): number {
  return v * v
}

function buildBgm(track: BgmTrack, volume: number): Howl | null {
  if (track === 'none') return null
  return new Howl({ src: [BGM_FILES[track]], loop: true, volume: toPerceptual(volume) })
}

export function useSound() {
  const store = useSoundStore()

  function initSound(): void {
    if (initialized) return
    initialized = true

    for (const [id, src] of Object.entries(MANIFEST) as [SoundId, string][]) {
      sfxPool.set(id, new Howl({ src: [src], preload: true, volume: toPerceptual(store.sfxVolume) }))
    }

    if (store.bgmTrack !== 'none' && !store.bgmMuted) {
      bgm = buildBgm(store.bgmTrack, store.bgmVolume)
      bgm?.play()
    }

    watchStops.push(
      watch(() => store.sfxVolume, (v) => {
        if (!store.sfxMuted) sfxPool.forEach(h => h.volume(toPerceptual(v)))
      }),
      watch(() => store.sfxMuted, (muted) => {
        sfxPool.forEach(h => h.volume(muted ? 0 : toPerceptual(store.sfxVolume)))
      }),
      watch(() => store.bgmVolume, (v) => {
        if (!store.bgmMuted) bgm?.volume(toPerceptual(v))
      }),
      watch(() => store.bgmMuted, (muted) => {
        if (!bgm) {
          if (!muted && store.bgmTrack !== 'none') {
            bgm = buildBgm(store.bgmTrack, store.bgmVolume)
            bgm?.play()
          }
          return
        }
        if (muted) { bgm.fade(bgm.volume() as number, 0, 500) }
        else        { bgm.volume(toPerceptual(store.bgmVolume)); if (!bgm.playing()) bgm.play() }
      }),
      watch(() => store.bgmTrack, (track) => {
        bgm?.stop(); bgm?.unload(); bgm = null
        if (track !== 'none' && !store.bgmMuted) {
          bgm = buildBgm(track, store.bgmVolume)
          bgm?.play()
        }
      }),
    )

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        Howler.ctx?.resume()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    watchStops.push(() => document.removeEventListener('visibilitychange', onVisible))
  }

  function destroySound(): void {
    initialized = false
    for (const stop of watchStops) stop()
    watchStops = []
    bgm?.stop(); bgm?.unload(); bgm = null
    sfxPool.forEach(h => { h.stop(); h.unload() })
    sfxPool.clear()
  }

const CHAT_SOUNDS: SoundId[] = [
  'chat_kapuchino', 'chat_capixie', 'chat_yanpai',
  'chat_xiaobiesan', 'chat_wolaitongniwan', 'chat_canshi', 'chat_zhefu',
]

  function play(id: SoundId): void {
    if (store.sfxMuted) return
    if (CHAT_SOUNDS.includes(id)) {
      for (const cid of CHAT_SOUNDS) sfxPool.get(cid)?.stop()
    }
    const h = sfxPool.get(id)
    if (!h) return
    h.volume(toPerceptual(store.sfxVolume))
    h.play()
  }

  return { initSound, destroySound, play }
}
